import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  APP_AUTH_LEVEL_PATH,
  APP_PRIVATE_PATHS,
  APP_TOP_LEVEL_PATH,
} from '@epicuro-next/navigation/constants';
import { addSeconds, isNonNullish, isNullish } from '@epicuro-next/utilities';
import {
  getPulseUIRedirectTo,
  setPulseUIRedirectTo,
} from '@epicuro-next/utilities-browser';
import { EMPTY, Observable, Subject, throwError } from 'rxjs';
import { catchError, filter, map, take } from 'rxjs/operators';

import {
  IAuthorization,
  IAuthorizationResponseContract,
} from '../models/authorization';
import { EventType } from '../models/broadcast-auth-event.interface';
import { TOKEN_API } from '../tokens';

import { BroadcastService } from './broadcast.service';

const NO_REFRESH_TOKEN_ERROR =
  'Pulse: acquireLegacyAuthorization null_refresh_token';
const INVALID_GRANT_ERROR = 'invalid_grant';

enum SearchStorageKeys {
  SEARCH_RECENT_PRODUCTS_STORE_KEY = 'searchRecentProductsHistory',
  SEARCH_QUERIES_STORE_KEY = 'searchQueriesHistory',
  PRODUCTS_SEARCH_TABLE_COMPONENT_STORE = 'products_search_table_component_store',
}

const cachedKeys = [
  SearchStorageKeys.SEARCH_RECENT_PRODUCTS_STORE_KEY,
  SearchStorageKeys.SEARCH_QUERIES_STORE_KEY,
  SearchStorageKeys.PRODUCTS_SEARCH_TABLE_COMPONENT_STORE,
];

@Injectable()
export class AuthenticationService {
  private acquireTokenInProgress = false;
  private authorizationSubject$ = new Subject<IAuthorization | null>();

  private authorization: IAuthorization;
  private get cacheStorage() {
    return localStorage;
  }

  public get accessToken() {
    if (
      !this.authorization?.accessToken ||
      this.authorization?.expires.getTime() < Date.now()
    ) {
      return null;
    }

    return this.authorization?.accessToken;
  }

  public get refreshToken() {
    return (
      this.authorization?.refreshToken ?? this.getFromStorage('refreshToken')
    );
  }

  public get loginPath() {
    return [
      '/',
      APP_TOP_LEVEL_PATH.authentication.path,
      APP_AUTH_LEVEL_PATH.login.path,
    ];
  }

  constructor(
    private http: HttpClient,
    private broadcastService: BroadcastService,
    private router: Router,
  ) {}

  public acquireTokenSilent(): Observable<IAuthorization> {
    if (!this.acquireTokenInProgress) {
      this.broadcastService.emit({
        type: EventType.ACQUIRE_TOKEN_INIT,
      });
      this.acquireTokenInProgress = true;

      this.acquireLegacyAuthorization()
        .pipe(
          map((authResponse) => {
            return this.finishAcquire(authResponse);
          }),
          catchError((error) => {
            this.acquireTokenInProgress = false;

            this.broadcastService.emit({
              type: EventType.ACQUIRE_TOKEN_FAILURE,
            });

            this.loginRedirect();

            if (
              NO_REFRESH_TOKEN_ERROR === error.message ||
              error?.error?.error === INVALID_GRANT_ERROR
            ) {
              return EMPTY;
            }

            throw error;
          }),
        )
        .subscribe();
    }

    return this.authorizationSubject$.pipe(
      filter((value) => !!value),
      take(1),
      map((value: IAuthorization) => {
        return value;
      }),
    );
  }

  private acquireLegacyAuthorization(): Observable<IAuthorization> {
    const refreshToken = this.getFromStorage('refreshToken');
    if (isNullish(refreshToken)) {
      return throwError(() => new Error(NO_REFRESH_TOKEN_ERROR));
    }

    const bodyParams = new HttpParams()
      .set('session_id', this.getFromStorage('sessionId'))
      .set('refresh_token', refreshToken)
      .set('grant_type', 'refresh_token');

    return this.http
      .post<IAuthorizationResponseContract>(TOKEN_API, bodyParams)
      .pipe(map(mapToToken));
  }

  private getFromStorage(itemKey: string) {
    return this.cacheStorage.getItem(itemKey) as string;
  }

  private setToStorage(key: string, value: string) {
    this.cacheStorage.setItem(key, value);
  }

  public loginRedirect() {
    if (location.pathname.includes('/authentication')) {
      return;
    }

    this.writeRedirectLinkToStorage();
    this.clearCacheStorageExceptAppRedirect();
    this.router.navigate(this.loginPath, { queryParamsHandling: 'preserve' });
  }

  public logout() {
    this.preserveSearchQueriesAndClearStorage();
    this.router.navigate(this.loginPath, { queryParamsHandling: 'preserve' });
  }

  public loginSuccess(authorizationResponse: IAuthorizationResponseContract) {
    const authorization = mapToToken(authorizationResponse);

    this.setToStorage('refreshToken', authorization.refreshToken);
    this.setToStorage('sessionId', authorization.sessionId);
    this.finishAcquire(authorization);
  }

  private writeRedirectLinkToStorage() {
    const sessionRedirectTo = getPulseUIRedirectTo();
    const initialPulseRedirectUri = getPulsePath(window.location.href);

    const currentPulsePath = getPulsePath(
      window.location.pathname + window.location.search,
    );
    const pathToRedirect =
      currentPulsePath || sessionRedirectTo || initialPulseRedirectUri;

    if (
      isNonNullish(pathToRedirect) &&
      pathToRedirect.length > 1 && // cover when '' and '/'
      pathToRedirect.replace('/', '') !== APP_PRIVATE_PATHS.home.path &&
      pathToRedirect.replace('/', '') !==
        APP_TOP_LEVEL_PATH.accountSelector.path &&
      pathToRedirect.replace('/', '') !== APP_TOP_LEVEL_PATH.userTerms.path &&
      pathToRedirect.replace('/', '') !== APP_TOP_LEVEL_PATH.siteSelector.path
    ) {
      setPulseUIRedirectTo(pathToRedirect);
    }
  }

  private clearCacheStorageExceptAppRedirect() {
    const redirectTo = getPulseUIRedirectTo();
    this.preserveSearchQueriesAndClearStorage();
    setPulseUIRedirectTo(redirectTo);
  }

  private finishAcquire(authResponse: IAuthorization) {
    this.acquireTokenInProgress = false;

    this.authorization = authResponse;
    this.broadcastService.emit({
      type: EventType.ACQUIRE_TOKEN_SUCCESS,
      payload: { accessToken: authResponse.accessToken },
    });
    this.authorizationSubject$.next(authResponse);

    return authResponse;
  }

  private preserveSearchQueriesAndClearStorage() {
    const cachedValues = cachedKeys.reduce((acc, key) => {
      const values = this.cacheStorage.getItem(key);
      if (values) {
        return [...acc, [key, values]];
      }
      return acc;
    }, []);

    this.cacheStorage.clear();

    cachedValues.forEach(([key, values]) => {
      this.cacheStorage.setItem(key, values);
    });
  }
}

function mapToToken(
  tokenResponse: IAuthorizationResponseContract,
): IAuthorization {
  return {
    accessToken: tokenResponse.access_token,
    refreshToken: tokenResponse.refresh_token,
    expires: tokenResponse.expires_in
      ? addSeconds(new Date(), tokenResponse.expires_in)
      : new Date(Date.parse(tokenResponse['.expires'])),
    sessionId: tokenResponse.session_id,
  };
}

function getPulsePath(url?: string) {
  return (url?.split(`${window.location.origin}/`) ?? [])[1];
}
